import {
  configureStore,
  createListenerMiddleware,
  type PayloadAction,
} from "@reduxjs/toolkit";
import {
  actionTypes,
  router5Middleware,
  router5Reducer,
  actions as routerActions,
} from "redux-router5";
import createRouter, { type State as RouterState } from "router5";
import browserPluginFactory from "router5-plugin-browser";
import { UNPAGED_LIMIT } from "../common/constants/constants";
import {
  arrayToObject,
  resolveMissingEntityMap,
  responseUnwrap,
} from "../common/functions";
import { collectReferencedIds } from "./ticket/worklog/workLogFormatter";
import { publicRoutes, routeNames, routes } from "./common/routes";
import { appFeedbackSlice } from "../features/app-feedback/appFeedbackSlice";
import componentReducer, { componentActions } from "../features/component/componentSlice";
import dashboardReducer, { dashboardActions } from "../features/dashboard/dashboardSlice";
import clientReducer, { clientActions } from "../features/client/clientSlice";
import intlSliceReducer from "../features/intl/intlSlice";
import locationReducer, { locationActions } from "../features/location/locationSlice";
import materialsReducer, { materialsActions } from "../features/materials/materialsSlice";
import notificationReducer, {
  notificationActions,
} from "../features/notification/notificationSlice";
import assignmentScheduleReducer, {
  assignmentScheduleActions,
  type ScheduleChangeSource,
} from "../features/assignment-schedule/assignmentScheduleSlice";
import projectReducer, { projectActions } from "../features/project/projectSlice";
import reportsReducer, { reportsActions } from "../features/reports/reportsSlice";
import servicesReducer, { servicesActions } from "../features/services/servicesSlice";
import ticketDataReducer, {
  ticketDataActions,
} from "../features/ticket-data/ticketDataSlice";
import ticketReducer, {
  ticketActions,
  type SideloadedTicketInfo,
  type TicketDateEditPayload,
} from "../features/ticket/ticketSlice";
import tokenReducer from "../features/token/tokenSlice";
import serviceCounterReducer from "../features/service-count/serviceCountSlice";
import pageStatusReducer, {
  pageStatusActions,
} from "../features/page-status/pageStatusSlice";
import turbineReducer, {
  turbineActions,
  type SideloadedTurbineInfo,
} from "../features/turbine/turbineSlice";
import userManagementReducer, {
  userManagementActions,
} from "../features/user-management/userManagementSlice";
import userSliceReducer, { userActions } from "../features/user/userSlice";
import type {
  ApiV1ManagementClientsTurbinesListSideloadEnum,
  ApiV1ManagementTechTurbinesListSideloadEnum,
  ApiV1ManagementTicketListExcludeStatusEnum,
  ApiV1ManagementTicketListSideloadEnum,
  ApiV1ManagementTurbinesListSideloadEnum,
  TurbineComponent,
  Client,
  Location,
  Materials,
  MaterialUsageSummary,
  ModelFile,
  Notification as DelayNotification,
  NotificationList,
  PaginatedClientList,
  PaginatedMaterialsList,
  PaginatedProjectWithWorkOrderCountsList,
  DashboardSummary,
  PaginatedServicesList,
  PaginatedTechnicianAssignmentScheduleList,
  BookableWorkOrder,
  ManualAssignment,
  PaginatedUserList,
  Project,
  ProjectDetailed,
  ProjectReportSummary,
  TechnicianAssignmentSchedule,
  TechniciansReportSummary,
  Services,
  Ticket,
  TicketData,
  TicketRetrieve,
  TicketStandbyPeriod,
  TimeTracking,
  Turbine,
  TurbineRetrieve,
  TurbineWorkSummary,
  User,
  UserGroup,
  UserProfile,
  WorkLog,
} from "./generated-axios-client";
import type { Err } from "../types/err";
import type { NavigatePayload } from "../types/router";
import { client } from "./api-client";
import {
  downloadBlob,
  filenameFromContentDisposition,
} from "../common/utils/downloadBlob";
export type RootState = ReturnType<ReturnType<typeof confStore>["getState"]>;

export type AppDispatch = typeof store.dispatch;

type NavigateAction = PayloadAction<NavigatePayload>;
const isNavigateAction = (
  action: NavigateAction,
  nameMatcher: string | ((name: string) => boolean),
  _args?: object
) => {
  if (nameMatcher instanceof Function) {
    return (
      action.type === actionTypes.TRANSITION_SUCCESS &&
      nameMatcher(action.payload.route.name)
    );
  }
  return (
    action.type === actionTypes.TRANSITION_SUCCESS &&
    action.payload.route.name === nameMatcher
  );
};

// Marks the page for the navigated route as failed so LandingPage renders an error page instead of a skeleton
function pageLoadErrorFor(navigateAction: NavigateAction, error: Err) {
  return pageStatusActions.setPageLoadError({
    status: error.status ?? 500,
    routePath: navigateAction.payload.route.path,
  });
}

function handleErrorNotification(state: RootState, error: Err) {
  const infoFunc = state.appFeedback.notification?.info;
  const intl = state.intl.datetimeIntl;
  if ("error" in error) {
    infoFunc?.({
      title: intl.formatMessage({ id: `${error?.data?.code}` }),
    });
  }
}

const VALID_PAGE_SIZES = [10, 25, 50, 100] as const;

function getPerPage(params: Record<string, unknown>): number | undefined {
  const fromRoute = params?.per_page;
  if (fromRoute) {
    const n = Number(fromRoute);
    if ((VALID_PAGE_SIZES as readonly number[]).includes(n)) return n;
  }
  const stored = localStorage.getItem("per_page");
  if (stored) {
    const n = parseInt(stored, 10);
    if ((VALID_PAGE_SIZES as readonly number[]).includes(n)) return n;
  }
  return undefined;
}

function fetchUserList(params: RouterState["params"] = {}) {
  return client.apiV1ManagementUserManagementList(
    params.page,
    params.email,
    params.groups__name,
    params.is_active,
    params.sort,
    getPerPage(params),
    params.search,
    params.technician_level
  );
}

async function fetchAndStoreTicketData(
  dispatch: AppDispatch,
  filters: Partial<Record<string, string>> = {}
) {
  const response = await client.apiV1ManagementWorkOrdersMetaRetrieve(
    filters.client,
    filters.location,
    filters.turbine
  );
  const unwrappedResponse = responseUnwrap<TicketData>(response);

  if (!("error" in unwrappedResponse)) {
    dispatch(ticketDataActions.addTicketData(unwrappedResponse));
    // Clear stale components when switching client — they belong to the old turbine
    if (filters.client && !filters.turbine && !filters.location) {
      dispatch(ticketDataActions.clearTicketDataComponents());
    }
  }
}

export const listener = createListenerMiddleware();

//fetch ticket data for ticket creation
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketDataActions.fetchTicketData,
  effect: async (action, { dispatch }) => {
    await fetchAndStoreTicketData(dispatch, action.payload);
  },
});

// add or update ticket
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.addOrUpdateTicket,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;

    if (!action.payload.id) {
      const addTicketResponse = await client.apiV1ManagementTicketCreate(action.payload);
      const unwrappedAddTicketResponse = responseUnwrap<Ticket>(addTicketResponse);

      if (!("error" in unwrappedAddTicketResponse)) {
        action.payload = unwrappedAddTicketResponse;
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_ticket_create_success",
          }),
        });
        dispatch(routerActions.navigateTo(routeNames.TICKET_LIST));
      }
    } else {
      const updateTicketResponse = await client.apiV1ManagementTicketUpdate(
        action.payload.id,
        action.payload
      );
      const unwrappedUpdateTicketResponse = responseUnwrap<Ticket>(updateTicketResponse);
      if (!("error" in unwrappedUpdateTicketResponse)) {
        action.payload = unwrappedUpdateTicketResponse;
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_ticket_update_success",
          }),
        });
        dispatch(
          routerActions.navigateTo(routeNames.TICKET_VIEW, {
            id: action.payload.id,
          })
        );
      }
    }
  },
});

//fetch ticket on edit or view action
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TICKET_EDIT) ||
    isNavigateAction(action as NavigateAction, routeNames.TICKET_VIEW),
  effect: async (action, { dispatch }) => {
    dispatch(ticketActions.removeTicket());
    const navigateAction = action as NavigateAction;
    const ticketId = navigateAction.payload.route.params.id;

    const getTicketResponse = await client.apiV1ManagementTicketRetrieve(ticketId);

    const unwrappedResponse = responseUnwrap<TicketRetrieve>(getTicketResponse);
    if ("error" in unwrappedResponse) {
      dispatch(pageLoadErrorFor(navigateAction, unwrappedResponse));
      return;
    }
    dispatch(ticketActions.storeTicket(unwrappedResponse));

    if (isNavigateAction(action as NavigateAction, routeNames.TICKET_VIEW)) {
      // WO History merges worklog, standby and rejection entries into one stream and
      // sorts it newest-first in the browser. That is only correct because the requests
      // below are unpaged (UNPAGED_LIMIT): the worklog endpoint has no .order_by(), so a
      // real page size would hand back an arbitrary SUBSET and the merged view would
      // present a confidently wrong order with no visible error. The standby periods are
      // unpaged for the same reason — they are matched one-to-one against the resume rows,
      // and a missing period would leave its row without a duration. Lowering the limit or
      // introducing pagination here requires ordering the worklog queryset server-side.
      const [
        sessionsRes,
        worklogRes,
        delaysRes,
        rejectionsRes,
        standbyPeriodsRes,
        filesRes,
      ] = await Promise.all([
          client.apiV1ManagementTimeTrackingList(
            undefined,
            undefined,
            "-started_at",
            UNPAGED_LIMIT,
            ticketId
          ),
          client.apiV1ManagementWorklogList(ticketId, undefined, UNPAGED_LIMIT),
          client.apiV1ManagementTicketDelaysList(
            undefined,
            "-created_at",
            UNPAGED_LIMIT,
            undefined,
            undefined,
            undefined,
            ticketId
          ),
          client.apiV1ManagementTicketRejectionsList(
            undefined,
            "-created_at",
            UNPAGED_LIMIT,
            undefined,
            undefined,
            undefined,
            ticketId
          ),
          client.apiV1ManagementTicketStandbyPeriodsList(
            undefined,
            "started_at",
            UNPAGED_LIMIT,
            ticketId
          ),
          client.apiV1ManagementFilesList(
            undefined,
            undefined,
            ticketId,
            undefined,
            undefined,
            UNPAGED_LIMIT
          ),
        ]);
      dispatch(
        ticketActions.storeTimeSessions(
          (sessionsRes.data?.results ?? []) as TimeTracking[]
        )
      );
      const unwrappedFiles = responseUnwrap(filesRes);
      if (!("error" in unwrappedFiles)) {
        dispatch(ticketActions.storeTicketFiles(unwrappedFiles.results as ModelFile[]));
      }
      const worklogEntries = (worklogRes.data?.results ?? []) as WorkLog[];
      dispatch(ticketActions.storeWorklogEntries(worklogEntries));
      dispatch(
        ticketActions.storeTicketDelays(
          (delaysRes.data?.results ?? []) as DelayNotification[]
        )
      );
      dispatch(
        ticketActions.storeTicketRejections(
          (rejectionsRes.data?.results ?? []) as DelayNotification[]
        )
      );
      dispatch(
        ticketActions.storeStandbyPeriods(
          (standbyPeriodsRes.data?.results ?? []) as TicketStandbyPeriod[]
        )
      );

      // Resolve location/turbine/client/project names referenced in this ticket's
      // worklog history — seeded for free from the ticket's own nested objects,
      // only fetching ids that differ from the ticket's current values (i.e. past
      // reassignments), never the full catalog.
      const currentTicket = !("error" in unwrappedResponse) ? unwrappedResponse : null;
      const seedLocationMap: Record<string, string> = currentTicket?.location
        ? { [currentTicket.location.id!]: currentTicket.location.name ?? "" }
        : {};
      const seedTurbineMap: Record<string, string> = currentTicket?.turbine
        ? { [currentTicket.turbine.id!]: currentTicket.turbine.serial_number ?? "" }
        : {};
      const seedClientMap: Record<string, string> = currentTicket?.client
        ? { [currentTicket.client.id!]: currentTicket.client.legal_name ?? "" }
        : {};
      const seedProjectMap: Record<string, string> = currentTicket?.project
        ? { [currentTicket.project.id!]: currentTicket.project.name ?? "" }
        : {};

      const referenced = collectReferencedIds(worklogEntries, [
        "location",
        "turbine",
        "client",
        "project",
      ]);

      const [locationMap, turbineMap, clientMap, projectMap] = await Promise.all([
        resolveMissingEntityMap<Location>(
          referenced.location,
          seedLocationMap,
          (id) => client.apiV1ManagementLocationsRetrieve(id),
          (l) => l.name ?? ""
        ),
        resolveMissingEntityMap<TurbineRetrieve>(
          referenced.turbine,
          seedTurbineMap,
          (id) => client.apiV1ManagementTurbinesRetrieve(id),
          (t) => t.serial_number ?? ""
        ),
        resolveMissingEntityMap<Client>(
          referenced.client,
          seedClientMap,
          (id) => client.apiV1ManagementClientsRetrieve(id),
          (c) => c.legal_name ?? ""
        ),
        resolveMissingEntityMap<ProjectDetailed>(
          referenced.project,
          seedProjectMap,
          (id) => client.apiV1ManagementProjectsRetrieve(id),
          (p) => p.name ?? ""
        ),
      ]);
      dispatch(
        ticketActions.storeWorklogEntityMaps({
          location: locationMap,
          turbine: turbineMap,
          client: clientMap,
          project: projectMap,
        })
      );
    }
  },
});

//fetch all ticket data when navigating to ticket edit so dropdowns are fully populated
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TICKET_EDIT),
  effect: async (_action, { dispatch }) => {
    dispatch(ticketDataActions.fetchTicketData({}));
  },
});

//remove ticket from state and add ticket data to state
//when navigating to ticket creation
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TICKET_CREATE) ||
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_CREATE) ||
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_EDIT),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    const params = navigateAction.payload.route.params;
    dispatch(ticketActions.removeTicket());

    // The unfiltered fetch has to come first and finish before the prefill one:
    // a client+turbine response only narrows turbines, components, locations and
    // projects, so on its own it never delivers the clients and technicians the
    // create form waits for before it renders. Doing it the other way round would
    // also let the unfiltered response wipe the prefilled turbine's components.
    await fetchAndStoreTicketData(dispatch);

    const prefillFilters = {
      client: params?.client,
      turbine: params?.turbine,
    };
    if (prefillFilters.client || prefillFilters.turbine) {
      await fetchAndStoreTicketData(dispatch, prefillFilters);
    }
  },
});

//load client tickets when navigating to client view
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.CLIENT_VIEW),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    const response = await client.apiV1ManagementTicketList(
      undefined,
      undefined,
      navigateAction.payload.route.params.id,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      [
        "assignees,created_by,turbine,location, client, coordinators, managers" as ApiV1ManagementTicketListSideloadEnum,
      ],
      undefined,
      undefined
    );
    const unwrappedResponse = responseUnwrap(response);
    if (!("error" in unwrappedResponse)) {
      dispatch(ticketActions.addTicketList(unwrappedResponse));

      const sideload = {} as SideloadedTicketInfo;
      for (const [key, value] of Object.entries(unwrappedResponse.results)) {
        sideload[key as keyof typeof sideload] = arrayToObject(value, "id");
      }
      dispatch(ticketActions.addTicketSideload(sideload));
    }
  },
});

// save ticket items (add or update tasks)
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.saveTicketItems,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { ticketId, items, materialIds } = action.payload;

    const response = await client.apiV1ManagementTicketPartialUpdate(ticketId, {
      items,
      ...(materialIds && {
        materials: materialIds.map((materialId) => ({ material: materialId })),
      }),
    });
    if (response.status === 200) {
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_ticket_update_success" }),
      });
      const ticketResponse = await client.apiV1ManagementTicketRetrieve(ticketId);
      const unwrapped = responseUnwrap<TicketRetrieve>(ticketResponse);
      if (!("error" in unwrapped)) {
        dispatch(ticketActions.storeTicket(unwrapped));
      }
    }
  },
});

// start work order
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.startWorkOrder,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const id = action.payload;
    // "End Standby" and "Start Work Order" share this action; only the former
    // offers the standby-end clipboard message.
    const wasOnStandby =
      state.ticket.ticket?.id === id && state.ticket.ticket?.status === "STANDBY";

    const response =
      await client.apiV1ManagementTechnicianWorkOrdersStartPartialUpdate(id);
    if (response.status === 200) {
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_ticket_start_success" }),
      });
      if (wasOnStandby) {
        dispatch(
          ticketActions.setStandbyClipboardEvent({
            ticketId: id,
            kind: "end",
            occurredAt: new Date().toISOString(),
          })
        );
        // the Standby tab and WO History list the period that just closed
        dispatch(ticketActions.refreshTicketHistory(id));
      }
      const ticketResponse = await client.apiV1ManagementTicketRetrieve(id);
      const unwrapped = responseUnwrap<TicketRetrieve>(ticketResponse);
      if (!("error" in unwrapped)) {
        dispatch(ticketActions.storeTicket(unwrapped));
      }
    }
  },
});

// end work order (Done)
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.endWorkOrder,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { id, summaryText } = action.payload;

    const response = await client.apiV1ManagementTechnicianWorkOrdersEndPartialUpdate(
      id,
      {
        summary_text: summaryText,
      }
    );
    if (response.status === 200) {
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_ticket_end_success" }),
      });
      const ticketResponse = await client.apiV1ManagementTicketRetrieve(id);
      const unwrapped = responseUnwrap<TicketRetrieve>(ticketResponse);
      if (!("error" in unwrapped)) {
        dispatch(ticketActions.storeTicket(unwrapped));
      }
    }
  },
});

// confirm work order as done (coordinator review decision)
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.confirmWorkOrder,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const id = action.payload;

    // Dedicated approve endpoint — server sets status=DONE and resolution=APPROVED,
    // so no body is sent and neither field is set client-side.
    const response = await client.apiV1ManagementTicketTicketApprovePartialUpdate(id);
    if (response.status === 200) {
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_ticket_confirm_success" }),
      });
      const ticketResponse = await client.apiV1ManagementTicketRetrieve(id);
      const unwrapped = responseUnwrap<TicketRetrieve>(ticketResponse);
      if (!("error" in unwrapped)) {
        dispatch(ticketActions.storeTicket(unwrapped));
      }
    }
    // A non-200 (e.g. 400 ticket_must_be_in_review from a stale tab) is surfaced
    // by the global axios error interceptor — no per-listener handling needed.
  },
});

// reject work order back to in progress (coordinator review decision)
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.rejectWorkOrder,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { id, message } = action.payload;

    // Dedicated reject endpoint — server sets status=IN_PROGRESS and
    // resolution=REJECTED. The optional message becomes the rejection
    // notification text the technician reads.
    const response = await client.apiV1ManagementTicketTicketRejectPartialUpdate(id, {
      message,
    });
    if (response.status === 200) {
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_ticket_reject_success" }),
      });
      const [ticketResponse, rejectionsRes] = await Promise.all([
        client.apiV1ManagementTicketRetrieve(id),
        client.apiV1ManagementTicketRejectionsList(
          undefined,
          "-created_at",
          UNPAGED_LIMIT,
          undefined,
          undefined,
          undefined,
          id
        ),
      ]);
      const unwrapped = responseUnwrap<TicketRetrieve>(ticketResponse);
      if (!("error" in unwrapped)) {
        dispatch(ticketActions.storeTicket(unwrapped));
      }
      dispatch(
        ticketActions.storeTicketRejections(
          (rejectionsRes.data?.results ?? []) as DelayNotification[]
        )
      );
    }
    // A non-200 (e.g. 400 ticket_must_be_in_review from a stale tab) is surfaced
    // by the global axios error interceptor — no per-listener handling needed.
  },
});

// put work order on standby (report_delay/ is the standby endpoint, despite its name)
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.standbyWorkOrder,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { id, message, standby_to, standby_from } = action.payload;

    const response =
      await client.apiV1ManagementTechnicianWorkOrdersReportDelayPartialUpdate(id, {
        message,
        standby_to: standby_to ?? null,
        standby_from: standby_from ?? null,
      });
    if (response.status === 200) {
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_ticket_standby_success" }),
      });
      dispatch(
        ticketActions.setStandbyClipboardEvent({
          ticketId: id,
          kind: "start",
          reason: message,
          occurredAt: standby_from ?? new Date().toISOString(),
          plannedEnd: standby_to ?? null,
        })
      );
      // the Standby tab and WO History list the period that just opened
      dispatch(ticketActions.refreshTicketHistory(id));
      const [ticketResponse, delaysRes] = await Promise.all([
        client.apiV1ManagementTicketRetrieve(id),
        client.apiV1ManagementTicketDelaysList(
          undefined,
          "-created_at",
          UNPAGED_LIMIT,
          undefined,
          undefined,
          undefined,
          id
        ),
      ]);
      const unwrapped = responseUnwrap<TicketRetrieve>(ticketResponse);
      if (!("error" in unwrapped)) {
        dispatch(ticketActions.storeTicket(unwrapped));
      }
      dispatch(
        ticketActions.storeTicketDelays(
          (delaysRes.data?.results ?? []) as DelayNotification[]
        )
      );
    }
  },
});

// delay work order (manager-only hold for a client-side blocker)
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.delayWorkOrder,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { id, message } = action.payload;

    // Dedicated delay endpoint — the server records the status being left in
    // status_before_delay, which a plain PATCH to DELAYED would skip (400
    // use_delay_actions). The message becomes the delay notification assignees read.
    const response = await client.apiV1ManagementTicketTicketDelayPartialUpdate(id, {
      message,
    });
    if (response.status === 200) {
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_ticket_delay_success" }),
      });
      dispatch(ticketActions.refreshTicketDelays(id));
    }
    // A non-200 (e.g. 400 ticket_cannot_be_delayed from a stale tab) is surfaced by the
    // global axios error interceptor — no per-listener handling needed.
  },
});

// continue a delayed work order — the server restores its pre-delay status
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.continueWorkOrder,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const id = action.payload;

    const response = await client.apiV1ManagementTicketTicketContinuePartialUpdate(id);
    if (response.status === 200) {
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_ticket_continue_success" }),
      });
      dispatch(ticketActions.refreshTicketDelays(id));
    }
  },
});

// re-reads the ticket together with the delay/standby notification list the banners and
// the WO history are built from — both delay actions change the two in step
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.refreshTicketDelays,
  effect: async (action, { dispatch }) => {
    const id = action.payload;
    const [ticketResponse, delaysRes] = await Promise.all([
      client.apiV1ManagementTicketRetrieve(id),
      client.apiV1ManagementTicketDelaysList(
        undefined,
        "-created_at",
        UNPAGED_LIMIT,
        undefined,
        undefined,
        undefined,
        id
      ),
    ]);
    const unwrapped = responseUnwrap<TicketRetrieve>(ticketResponse);
    if (!("error" in unwrapped)) {
      dispatch(ticketActions.storeTicket(unwrapped));
    }
    dispatch(
      ticketActions.storeTicketDelays(
        (delaysRes.data?.results ?? []) as DelayNotification[]
      )
    );
  },
});

// refresh current ticket + time sessions (dispatched after a time entry edit)
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.refreshCurrentTicket,
  effect: async (action, { dispatch }) => {
    const id = action.payload;
    const [ticketResponse, sessionsRes] = await Promise.all([
      client.apiV1ManagementTicketRetrieve(id),
      client.apiV1ManagementTimeTrackingList(
        undefined,
        undefined,
        "-started_at",
        UNPAGED_LIMIT,
        id
      ),
    ]);
    const unwrapped = responseUnwrap<TicketRetrieve>(ticketResponse);
    if (!("error" in unwrapped)) {
      dispatch(ticketActions.storeTicket(unwrapped));
    }
    dispatch(
      ticketActions.storeTimeSessions((sessionsRes.data?.results ?? []) as TimeTracking[])
    );
  },
});

// edit the times of a check-in session or a manual task time log
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.editTimeEntry,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { entryId, ticketId, started_at, ended_at } = action.payload;

    // The body is the eighth positional argument; the six query params in between
    // are list filters the generated client attaches to every action on this
    // viewset and are unused here.
    const response = await client.apiV1ManagementTimeTrackingEditTimePartialUpdate(
      entryId,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      { started_at, ended_at }
    );
    if (response.status !== 200) {
      dispatch(ticketActions.setEditTimeEntryStatus("failed"));
      return;
    }
    dispatch(ticketActions.setEditTimeEntryStatus("succeeded"));
    infoFunc?.({
      title: intl.formatMessage({ id: "notification_time_entry_edit_success" }),
    });
    // The response carries the edited cohort, but total_time is derived from it and
    // lives on the ticket, so the ticket has to be re-read either way.
    dispatch(ticketActions.refreshCurrentTicket(ticketId));
  },
});

// re-read the ticket plus the two lists WO History is built from
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.refreshTicketHistory,
  effect: async (action, { dispatch }) => {
    const id = action.payload;
    const [ticketResponse, worklogRes, standbyPeriodsRes] = await Promise.all([
      client.apiV1ManagementTicketRetrieve(id),
      client.apiV1ManagementWorklogList(id, undefined, UNPAGED_LIMIT),
      client.apiV1ManagementTicketStandbyPeriodsList(
        undefined,
        "started_at",
        UNPAGED_LIMIT,
        id
      ),
    ]);
    const unwrapped = responseUnwrap<TicketRetrieve>(ticketResponse);
    if (!("error" in unwrapped)) {
      dispatch(ticketActions.storeTicket(unwrapped));
    }
    // worklogEntityMaps is deliberately left as the navigation listener built it: a
    // standby period edit logs only started_at/ended_at/message, which reference no
    // location, turbine, client or project the maps would need to resolve.
    dispatch(
      ticketActions.storeWorklogEntries((worklogRes.data?.results ?? []) as WorkLog[])
    );
    dispatch(
      ticketActions.storeStandbyPeriods(
        (standbyPeriodsRes.data?.results ?? []) as TicketStandbyPeriod[]
      )
    );
  },
});

// correct the start, end or reason of a recorded standby period
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.editStandbyPeriod,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { periodId, ticketId, started_at, ended_at, message } = action.payload;

    // Only these three fields are sent. PatchedTicketStandbyPeriod advertises the whole
    // model because partial_update has no request-schema override, but the API's edit
    // serializer accepts nothing else.
    const response = await client.apiV1ManagementTicketStandbyPeriodsPartialUpdate(
      periodId,
      { started_at, ended_at, message }
    );
    if (response.status !== 200) {
      dispatch(ticketActions.setEditStandbyPeriodStatus("failed"));
      return;
    }
    dispatch(ticketActions.setEditStandbyPeriodStatus("succeeded"));
    infoFunc?.({
      title: intl.formatMessage({ id: "notification_standby_period_edit_success" }),
    });
    // Total Standby Time lives on the ticket, the duration line comes from the periods
    // list, and the edit writes an audit row into the worklog — all three have moved.
    dispatch(ticketActions.refreshTicketHistory(ticketId));
  },
});

// set work order to In Review
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.reviewWorkOrder,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const id = action.payload;

    const response = await client.apiV1ManagementTechnicianWorkOrdersPartialUpdate(id, {
      status: "IN_REVIEW",
    });
    if (response.status === 200) {
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_ticket_review_success" }),
      });
      const ticketResponse = await client.apiV1ManagementTicketRetrieve(id);
      const unwrapped = responseUnwrap<TicketRetrieve>(ticketResponse);
      if (!("error" in unwrapped)) {
        dispatch(ticketActions.storeTicket(unwrapped));
      }
    }
  },
});

//delete ticket
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.deleteTicket,
  effect: async (action, { dispatch, getState }) => {
    const deleteResponse = await client.apiV1ManagementTicketDestroy(action.payload);
    if (deleteResponse.status === 204) {
      const state = getState();
      const infoFunc = state.appFeedback.notification?.info;
      const intl = state.intl.datetimeIntl;
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_ticket_delete_success" }),
      });

      const response = await client.apiV1ManagementTicketList(
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        [
          "assignees,created_by,turbine,location, client, coordinators, managers" as ApiV1ManagementTicketListSideloadEnum,
        ],
        undefined,
        undefined
      );

      const unwrappedResponse = responseUnwrap(response);
      if (!("error" in unwrappedResponse)) {
        dispatch(ticketActions.addTicketList(unwrappedResponse));

        const sideload = {} as SideloadedTicketInfo;
        for (const [key, value] of Object.entries(unwrappedResponse.results)) {
          sideload[key as keyof typeof sideload] = arrayToObject(value, "id");
        }
        dispatch(ticketActions.addTicketSideload(sideload));
      }
    }
  },
});

// update current user, statuses, services, user list on transition
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, (name) => !(name in publicRoutes)),
  effect: async (_action, { dispatch, getState }) => {
    const state = getState();

    // load current user
    if (state.userstate.user === undefined) {
      const userResponse = await client.apiV1ManagementMeRetrieve();
      const unwrappedUserResponse = responseUnwrap(userResponse);
      if (!("error" in unwrappedUserResponse)) {
        dispatch(userActions.getUser(unwrappedUserResponse));
      }
    }

    // load user list
    if (
      state.userstate.list.results === undefined ||
      state.userstate.list.results.length === 0
    ) {
      const userListResponse = await client.apiV1ManagementUsersList(
        1,
        undefined,
        undefined,
        undefined,
        UNPAGED_LIMIT
      );
      const unwrappedUserListResponse = responseUnwrap(userListResponse);
      if (!("error" in unwrappedUserListResponse)) {
        dispatch(userActions.getUserList(unwrappedUserListResponse));
      }
    }

    //load services
    if (state.ticket.services === null) {
      const serviceResponse = await client.apiV1ManagementServicesList(
        undefined,
        undefined,
        UNPAGED_LIMIT
      );
      const unwrappedServiceResponse = responseUnwrap(serviceResponse);
      if (!("error" in unwrappedServiceResponse)) {
        dispatch(ticketActions.addService(unwrappedServiceResponse.results));

        const serviceObj = arrayToObject(unwrappedServiceResponse.results, "id");
        dispatch(ticketActions.addServiceObj(serviceObj));
      }

      //load statuses
      if (state.ticket.statuses === null) {
        const statusResponse = await client.apiV1ManagementMetaTicketStatusesList();
        const unwrappedStatusResponse = responseUnwrap(statusResponse);
        if (!("error" in unwrappedStatusResponse)) {
          dispatch(ticketActions.addStatus(unwrappedStatusResponse));

          const statusObj = arrayToObject(unwrappedStatusResponse, "value");
          dispatch(ticketActions.addStatusObj(statusObj));
        }
      }
    }

    // load materials
    // if (
    // 	state.materials.list.results === undefined ||
    // 	state.materials.list.results.length === 0
    // ) {
    // 	const materialsResponse = await client.apiV1MaterialsList(
    // 		undefined,
    // 		undefined,
    // 		undefined,
    // 		undefined,
    // 		UNPAGED_LIMIT,
    // 	);
    // 	const unwrappedMaterials =
    // 		responseUnwrap<PaginatedMaterialsList>(materialsResponse);
    // 	if (!("error" in unwrappedMaterials)) {
    // 		dispatch(materialsActions.addMaterialList(unwrappedMaterials));
    // 	}
    // }
  },
});

//loads ticket list when navigating to ticket list page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TICKET_LIST),
  effect: async (action, { dispatch, getState }) => {
    dispatch(ticketDataActions.clearTicketData());
    const navigateAction = action as NavigateAction;
    const state = getState();
    const { status, search, attention } = navigateAction.payload.route.params;
    // "All Work Orders" hides Done ones unless the user is searching; they stay under Work Orders → Done
    const excludedStatus: ApiV1ManagementTicketListExcludeStatusEnum | undefined =
      !status && !search ? "DONE" : undefined;

    const response = await client.apiV1ManagementTicketList(
      navigateAction.payload.route.params.assignees,
      attention || undefined,
      navigateAction.payload.route.params.client,
      navigateAction.payload.route.params.coordinators,
      navigateAction.payload.route.params.created_by,
      navigateAction.payload.route.params.page,
      excludedStatus,
      undefined,
      navigateAction.payload.route.params.managers,
      navigateAction.payload.route.params.sort,
      getPerPage(navigateAction.payload.route.params),
      navigateAction.payload.route.params.project,
      navigateAction.payload.route.params.report_done,
      navigateAction.payload.route.params.search,
      navigateAction.payload.route.params.service,
      [
        "assignees,created_by,turbine,client,location,coordinators,managers" as ApiV1ManagementTicketListSideloadEnum,
      ],
      navigateAction.payload.route.params.status,
      navigateAction.payload.route.params.turbine
    );

    const unwrappedResponse = responseUnwrap(response);
    if (!("error" in unwrappedResponse)) {
      dispatch(ticketActions.addTicketList(unwrappedResponse));

      const sideload = {} as SideloadedTicketInfo;
      for (const [key, value] of Object.entries(unwrappedResponse.results)) {
        sideload[key as keyof typeof sideload] = arrayToObject(value, "id");
      }
      dispatch(ticketActions.addTicketSideload(sideload));
    }
  },
});

//fetch client list when navigating to client list page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.CLIENT_LIST),
  effect: async (action, { dispatch, getState }) => {
    const navigateAction = action as NavigateAction;
    const state = getState();

    const response = await client.apiV1ManagementClientsList(
      navigateAction.payload.route.params.page,
      navigateAction.payload.route.params.legal_name,
      navigateAction.payload.route.params.sort,
      getPerPage(navigateAction.payload.route.params),
      navigateAction.payload.route.params.search,
      navigateAction.payload.route.params.tax_id
    );

    const unwrappedResponse = responseUnwrap(response);
    if (!("error" in unwrappedResponse)) {
      dispatch(clientActions.addClientList(unwrappedResponse));
    }
  },
});

//fetch data when navigating to turbine create page, ticket list page, turbine list page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_CREATE) ||
    isNavigateAction(action as NavigateAction, routeNames.TICKET_LIST) ||
    isNavigateAction(action as NavigateAction, routeNames.TICKET_VIEW) ||
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_LIST) ||
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_EDIT),
  effect: async (_action, { dispatch, getState }) => {
    const state = getState();
    if (
      state.ticketData.clients === undefined ||
      state.ticketData.turbines === undefined ||
      state.ticketData.technicians === undefined ||
      state.ticketData.materials === undefined
    ) {
      const response = await client.apiV1ManagementWorkOrdersMetaRetrieve();
      const unwrappedResponse = responseUnwrap<TicketData>(response);

      if (!("error" in unwrappedResponse)) {
        dispatch(ticketDataActions.addTicketData(unwrappedResponse));
      }
    }
  },
});

//delete client
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: clientActions.deleteClient,
  effect: async (action, { dispatch, getState }) => {
    const deleteResponse = await client.apiV1ManagementClientsDestroy(action.payload);
    if (deleteResponse.status === 204) {
      const state = getState();
      const infoFunc = state.appFeedback.notification?.info;
      const intl = state.intl.datetimeIntl;
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_client_delete_success" }),
      });

      const response = await client.apiV1ManagementClientsList();

      const unwrappedResponse = responseUnwrap(response);
      if (!("error" in unwrappedResponse)) {
        dispatch(clientActions.addClientList(unwrappedResponse));
      }
    }
  },
});

// add or update client
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: clientActions.addOrUpdateClient,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;

    if (!action.payload.id) {
      const addClientResponse = await client.apiV1ManagementClientsCreate(action.payload);
      const unwrappedAddClientResponse = responseUnwrap<Client>(addClientResponse);

      if (!("error" in unwrappedAddClientResponse)) {
        action.payload = unwrappedAddClientResponse;
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_client_create_success",
          }),
        });
        dispatch(routerActions.navigateTo(routeNames.CLIENT_LIST));
      }
    } else {
      const updateClientResponse = await client.apiV1ManagementClientsPartialUpdate(
        action.payload.id,
        action.payload
      );
      const unwrappedUpdateClientResponse = responseUnwrap<Client>(updateClientResponse);
      if (!("error" in unwrappedUpdateClientResponse)) {
        action.payload = unwrappedUpdateClientResponse;
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_client_update_success",
          }),
        });
        dispatch(
          routerActions.navigateTo(routeNames.CLIENT_VIEW, {
            id: action.payload.id,
          })
        );
      }
    }
  },
});

//fetch client on edit or view action
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.CLIENT_EDIT) ||
    isNavigateAction(action as NavigateAction, routeNames.CLIENT_VIEW),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    const clientId = navigateAction.payload.route.params.id;
    dispatch(clientActions.removeClient());

    const getTicketResponse = await client.apiV1ManagementClientsRetrieve(clientId);

    const unwrappedResponse = responseUnwrap<Client>(getTicketResponse);
    if ("error" in unwrappedResponse) {
      dispatch(pageLoadErrorFor(navigateAction, unwrappedResponse));
      return;
    }
    dispatch(clientActions.storeClient(unwrappedResponse));
  },
});

//fetch client turbines on client view action
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.CLIENT_VIEW),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    const getTicketResponse = await client.apiV1ManagementClientsTurbinesList(
      navigateAction.payload.route.params.id,
      undefined,
      UNPAGED_LIMIT,
      ["client, location" as ApiV1ManagementClientsTurbinesListSideloadEnum]
    );

    const unwrappedResponse = responseUnwrap(getTicketResponse);
    if (!("error" in unwrappedResponse)) {
      dispatch(turbineActions.addTurbineList(unwrappedResponse));

      const sideload = {} as SideloadedTurbineInfo;
      for (const [key, value] of Object.entries(unwrappedResponse.results)) {
        sideload[key as keyof typeof sideload] = arrayToObject(value, "id");
      }
      dispatch(turbineActions.addTurbineSideload(sideload));
    }
  },
});

//fetch client projects on client view action
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.CLIENT_VIEW),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    const projectResponse = await client.apiV1ManagementProjectsList(
      navigateAction.payload.route.params.id
    );
    const unwrappedResponse = responseUnwrap<PaginatedProjectWithWorkOrderCountsList>(projectResponse);
    if (!("error" in unwrappedResponse)) {
      dispatch(projectActions.storeClientProjects(unwrappedResponse.results));
    }
  },
});

//fetch turbine specific work orders
listener.startListening.withTypes<AppDispatch>()({
  actionCreator: turbineActions.getTurbineWorkOrders,
  effect: async (action, { dispatch }) => {
    const getWorkOrdersResponse = await client.apiV1ManagementTurbinesWorkOrdersList(
      action.payload,
      undefined,
      UNPAGED_LIMIT,
      [
        "assignees,created_by,turbine,client,location,coordinators,managers" as ApiV1ManagementTicketListSideloadEnum,
      ]
    );
    const unwrappedResponse = responseUnwrap(getWorkOrdersResponse);
    if (!("error" in unwrappedResponse)) {
      dispatch(ticketActions.addTicketList(unwrappedResponse));

      const sideload = {} as SideloadedTicketInfo;
      for (const [key, value] of Object.entries(unwrappedResponse.results)) {
        sideload[key as keyof typeof sideload] = arrayToObject(value, "id");
      }
      dispatch(ticketActions.addTicketSideload(sideload));
    }
  },
});

//remove client from state when navigating to client creation
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.CLIENT_CREATE),
  effect: async (_action, { dispatch }) => {
    dispatch(clientActions.removeClient());
  },
});

// add or update turbine
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: turbineActions.addOrUpdateTurbine,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;

    if (!action.payload.id) {
      const addTurbineResponse = await client.apiV1ManagementTurbinesCreate(
        action.payload
      );
      const unwrappedAddTurbineResponse = responseUnwrap<Turbine>(addTurbineResponse);

      if (!("error" in unwrappedAddTurbineResponse)) {
        action.payload = unwrappedAddTurbineResponse;
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_turbine_create_success",
          }),
        });
        dispatch(
          routerActions.navigateTo(routeNames.TURBINE_VIEW, {
            id: unwrappedAddTurbineResponse.id,
          })
        );
      }
    } else {
      const updateTurbineResponse = await client.apiV1ManagementTurbinesPartialUpdate(
        action.payload.id,
        action.payload
      );
      const unwrappedUpdateTurbineResponse =
        responseUnwrap<Turbine>(updateTurbineResponse);
      if (!("error" in unwrappedUpdateTurbineResponse)) {
        action.payload = unwrappedUpdateTurbineResponse;
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_turbine_update_success",
          }),
        });
        dispatch(
          routerActions.navigateTo(routeNames.TURBINE_VIEW, {
            id: action.payload.id,
          })
        );
      }
    }
  },
});

// fetch turbine list when navigating to turbine list page
// and when creating tickets
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_LIST) ||
    isNavigateAction(action as NavigateAction, routeNames.TICKET_CREATE),
  effect: async (action, { dispatch, getState }) => {
    const navigateAction = action as NavigateAction;
    const state = getState();

    const isTurbineList = isNavigateAction(navigateAction, routeNames.TURBINE_LIST);
    const response = await client.apiV1ManagementTurbinesList(
      navigateAction.payload.route.params.client,
      navigateAction.payload.route.params.page,
      navigateAction.payload.route.params.location,
      navigateAction.payload.route.params.sort,
      isTurbineList ? getPerPage(navigateAction.payload.route.params) : undefined,
      navigateAction.payload.route.params.search,
      navigateAction.payload.route.params.serial_number,
      ["client,location" as ApiV1ManagementTurbinesListSideloadEnum]
    );

    const unwrappedResponse = responseUnwrap(response);
    if (!("error" in unwrappedResponse)) {
      dispatch(turbineActions.addTurbineList(unwrappedResponse));

      const sideload = {} as SideloadedTurbineInfo;
      for (const [key, value] of Object.entries(unwrappedResponse.results)) {
        sideload[key as keyof typeof sideload] = arrayToObject(value, "id");
      }
      dispatch(turbineActions.addTurbineSideload(sideload));
    }
  },
});

//fetch turbine on view and edit actions
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_EDIT) ||
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_VIEW),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    dispatch(turbineActions.clearTurbines());
    const getTicketResponse = await client.apiV1ManagementTurbinesRetrieve(
      navigateAction.payload.route.params.id
    );

    const unwrappedResponse = responseUnwrap<TurbineRetrieve>(getTicketResponse);
    if ("error" in unwrappedResponse) {
      dispatch(pageLoadErrorFor(navigateAction, unwrappedResponse));
      return;
    }
    dispatch(turbineActions.storeTurbine(unwrappedResponse));
  },
});

//remove turbine from state when navigating to turbine creation
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_CREATE),
  effect: async (action, { dispatch }) => {
    dispatch(turbineActions.removeTurbine(action.payload));
  },
});

//delete turbine
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: turbineActions.deleteTurbine,
  effect: async (action, { dispatch, getState }) => {
    const deleteResponse = await client.apiV1ManagementTurbinesDestroy(action.payload);
    if (deleteResponse.status === 204) {
      const state = getState();
      const infoFunc = state.appFeedback.notification?.info;
      const intl = state.intl.datetimeIntl;
      infoFunc?.({
        title: intl.formatMessage({
          id: "notification_turbine_delete_success",
        }),
      });

      const response = await client.apiV1ManagementTurbinesList(
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        ["client,location" as ApiV1ManagementTurbinesListSideloadEnum]
      );

      const unwrappedResponse = responseUnwrap(response);
      if (!("error" in unwrappedResponse)) {
        dispatch(turbineActions.addTurbineList(unwrappedResponse));

        const sideload = {} as SideloadedTurbineInfo;
        for (const [key, value] of Object.entries(unwrappedResponse.results)) {
          sideload[key as keyof typeof sideload] = arrayToObject(value, "id");
        }
        dispatch(turbineActions.addTurbineSideload(sideload));
      }
    }
  },
});

//fetch turbine components on turbine view
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_VIEW),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    const componentResponse = await client.apiV1ManagementTurbinesComponentsList(
      navigateAction.payload.route.params.id
    );

    const unwrappedResponse = responseUnwrap(componentResponse);
    if (!("error" in unwrappedResponse)) {
      dispatch(componentActions.addComponentList(unwrappedResponse));
    }
  },
});

//fetch turbine work orders on turbine view
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_VIEW),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    dispatch(turbineActions.getTurbineWorkOrders(navigateAction.payload.route.params.id));
  },
});

//fetch turbine work summary on turbine view
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TURBINE_VIEW),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    dispatch(
      turbineActions.fetchTurbineWorkSummary(navigateAction.payload.route.params.id)
    );
  },
});

// only the latest request may store its result, so a quick switch between
// turbines never shows the previous turbine's totals
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: turbineActions.fetchTurbineWorkSummary,
  effect: async (action, { dispatch, cancelActiveListeners, signal }) => {
    cancelActiveListeners();
    const response = await client.apiV1ManagementReportsTurbineSummaryRetrieve(
      action.payload,
      { signal }
    );
    if (signal.aborted) return;
    const unwrappedSummary = responseUnwrap<TurbineWorkSummary>(response);
    if ("error" in unwrappedSummary) {
      dispatch(turbineActions.setTurbineWorkSummaryFailed());
      return;
    }
    dispatch(turbineActions.storeTurbineWorkSummary(unwrappedSummary));
  },
});

//update component
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: componentActions.componentEdit,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    if (action.payload.id) {
      const updateComponentResponse = await client.apiV1ManagementComponentsPartialUpdate(
        action.payload.id,
        action.payload.data
      );
      const unwrappedResponse = responseUnwrap<TurbineComponent>(updateComponentResponse);
      if (!("error" in unwrappedResponse) && unwrappedResponse.turbine) {
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_component_update_success",
          }),
        });

        const componentResponse = await client.apiV1ManagementTurbinesComponentsList(
          unwrappedResponse.turbine
        );
        const unwrappedComponentResponse = responseUnwrap(componentResponse);
        if (!("error" in unwrappedComponentResponse)) {
          dispatch(componentActions.addComponentList(unwrappedComponentResponse));
        }
      }
    }
  },
});

// inline date edit from Work Orders list
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.ticketDateEdit,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { id, field, value } = action.payload as TicketDateEditPayload;

    const response = await client.apiV1ManagementTicketPartialUpdate(id, {
      [field]: value,
    });
    const unwrapped = responseUnwrap<Ticket>(response);
    if (!("error" in unwrapped)) {
      dispatch(ticketActions.updateTicketDateInList({ id, field, value }));
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_ticket_update_success" }),
      });
    } else {
      handleErrorNotification(state, unwrapped);
    }
  },
});

// inline assignee edit from Work Orders list
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.ticketAssigneesEdit,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { id, assigneeIds } = action.payload;

    const response = await client.apiV1ManagementTicketPartialUpdate(id, {
      assignees: assigneeIds.map((assignee) => ({ assignee })),
    });
    const unwrapped = responseUnwrap<Ticket>(response);
    if (!("error" in unwrapped)) {
      const assignedUsers = (state.ticketData.technicians ?? []).filter((technician) =>
        assigneeIds.includes(technician.id)
      );
      dispatch(
        ticketActions.updateTicketAssigneesInList({
          id,
          assignees: unwrapped.assignees,
          users: assignedUsers,
        })
      );
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_ticket_update_success" }),
      });
    } else {
      handleErrorNotification(state, unwrapped);
    }
  },
});

// fetch location list when navigating to location list page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.LOCATION_LIST),
  effect: async (action, { dispatch, getState }) => {
    const navigateAction = action as NavigateAction;
    const state = getState();

    const response = await client.apiV1ManagementLocationsList(
      navigateAction.payload.route.params.page,
      navigateAction.payload.route.params.sort,
      getPerPage(navigateAction.payload.route.params),
      navigateAction.payload.route.params.search
    );
    const unwrappedResponse = responseUnwrap(response);
    if (!("error" in unwrappedResponse)) {
      dispatch(locationActions.addLocationList(unwrappedResponse));
    }
  },
});

// fetch location on edit or view
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.LOCATION_EDIT) ||
    isNavigateAction(action as NavigateAction, routeNames.LOCATION_VIEW),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    dispatch(locationActions.clearLocation());
    const response = await client.apiV1ManagementLocationsRetrieve(
      navigateAction.payload.route.params.id
    );
    const unwrappedResponse = responseUnwrap<Location>(response);
    if ("error" in unwrappedResponse) {
      dispatch(pageLoadErrorFor(navigateAction, unwrappedResponse));
      return;
    }
    dispatch(locationActions.storeLocation(unwrappedResponse));
  },
});

// fetch turbines for location on location view
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.LOCATION_VIEW),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    const locationId = navigateAction.payload.route.params.id;
    const response = await client.apiV1ManagementTechTurbinesList(
      undefined,
      undefined,
      locationId,
      undefined,
      undefined,
      undefined,
      ["client,location" as ApiV1ManagementTechTurbinesListSideloadEnum]
    );
    const unwrappedResponse = responseUnwrap(response);
    if (!("error" in unwrappedResponse)) {
      dispatch(turbineActions.addTurbineList(unwrappedResponse));

      const sideload = {} as SideloadedTurbineInfo;
      for (const [key, value] of Object.entries(unwrappedResponse.results)) {
        sideload[key as keyof typeof sideload] = arrayToObject(value, "id");
      }
      dispatch(turbineActions.addTurbineSideload(sideload));
    }
  },
});

// clear location when navigating to create
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.LOCATION_CREATE),
  effect: async (_action, { dispatch }) => {
    dispatch(locationActions.clearLocation());
  },
});

// add or update location
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: locationActions.addOrUpdateLocation,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;

    if (!action.payload.id) {
      const response = await client.apiV1ManagementLocationsCreate(action.payload);
      const unwrapped = responseUnwrap<Location>(response);
      if (!("error" in unwrapped)) {
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_location_create_success",
          }),
        });
        dispatch(routerActions.navigateTo(routeNames.LOCATION_LIST));
      }
    } else {
      const response = await client.apiV1ManagementLocationsPartialUpdate(
        action.payload.id,
        action.payload
      );
      const unwrapped = responseUnwrap<Location>(response);
      if (!("error" in unwrapped)) {
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_location_update_success",
          }),
        });
        dispatch(
          routerActions.navigateTo(routeNames.LOCATION_VIEW, {
            id: action.payload.id,
          })
        );
      }
    }
  },
});

// delete location
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: locationActions.deleteLocation,
  effect: async (action, { dispatch, getState }) => {
    const deleteResponse = await client.apiV1ManagementLocationsDestroy(action.payload);
    if (deleteResponse.status === 204) {
      const state = getState();
      const infoFunc = state.appFeedback.notification?.info;
      const intl = state.intl.datetimeIntl;
      infoFunc?.({
        title: intl.formatMessage({
          id: "notification_location_delete_success",
        }),
      });
      const response = await client.apiV1ManagementLocationsList();
      const unwrapped = responseUnwrap(response);
      if (!("error" in unwrapped)) {
        dispatch(locationActions.addLocationList(unwrapped));
      }
    }
  },
});

// load user management list when navigating to user list page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.SETTINGS_USER_LIST),
  effect: async (action, { dispatch, getState }) => {
    const navigateAction = action as NavigateAction;
    const state = getState();

    const [response, groupsResponse] = await Promise.all([
      fetchUserList(navigateAction.payload.route.params),
      client.apiV1ManagementMetaGroupsList(),
    ]);
    const unwrapped = responseUnwrap<PaginatedUserList>(response);
    if (!("error" in unwrapped)) {
      dispatch(userManagementActions.addUserList(unwrapped));
    }
    const groupsUnwrapped = responseUnwrap<UserGroup[]>(groupsResponse);
    if (!("error" in groupsUnwrapped)) {
      dispatch(userManagementActions.storeGroups(groupsUnwrapped));
    }
  },
});

// fetch single user when navigating to edit page
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.SETTINGS_USER_EDIT),
  effect: async (action, { dispatch }) => {
    dispatch(userManagementActions.removeUser());
    const navigateAction = action as NavigateAction;
    const [response, groupsResponse] = await Promise.all([
      client.apiV1ManagementUserManagementRetrieve(
        navigateAction.payload.route.params.id
      ),
      client.apiV1ManagementMetaGroupsList(),
    ]);
    const groupsUnwrapped = responseUnwrap<UserGroup[]>(groupsResponse);
    if (!("error" in groupsUnwrapped)) {
      dispatch(userManagementActions.storeGroups(groupsUnwrapped));
    }
    const unwrapped = responseUnwrap<User>(response);
    if ("error" in unwrapped) {
      dispatch(pageLoadErrorFor(navigateAction, unwrapped));
      return;
    }
    dispatch(userManagementActions.storeUser(unwrapped));
  },
});

// clear user when navigating to create page
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.SETTINGS_USER_CREATE),
  effect: async (_action, { dispatch }) => {
    dispatch(userManagementActions.removeUser());
    const groupsResponse = await client.apiV1ManagementMetaGroupsList();
    const groupsUnwrapped = responseUnwrap<UserGroup[]>(groupsResponse);
    if (!("error" in groupsUnwrapped)) {
      dispatch(userManagementActions.storeGroups(groupsUnwrapped));
    }
  },
});

// create or update user
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: userManagementActions.addOrUpdateUser,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;

    if (!action.payload.id) {
      const response = await client.apiV1ManagementUserManagementCreate(action.payload);
      const unwrapped = responseUnwrap<User>(response);
      if (!("error" in unwrapped)) {
        infoFunc?.({
          title: intl.formatMessage({ id: "notification_user_create_success" }),
        });
        dispatch(routerActions.navigateTo(routeNames.SETTINGS_USER_LIST));
      }
    } else {
      const response = await client.apiV1ManagementUserManagementPartialUpdate(
        action.payload.id,
        action.payload
      );
      const unwrapped = responseUnwrap<User>(response);
      if (!("error" in unwrapped)) {
        infoFunc?.({
          title: intl.formatMessage({ id: "notification_user_update_success" }),
        });
        dispatch(routerActions.navigateTo(routeNames.SETTINGS_USER_LIST));
      }
    }
  },
});

// delete user
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: userManagementActions.deleteUser,
  effect: async (action, { dispatch, getState }) => {
    const deleteResponse = await client.apiV1ManagementUserManagementDestroy(
      action.payload
    );
    if (deleteResponse.status === 204) {
      const state = getState();
      const infoFunc = state.appFeedback.notification?.info;
      const intl = state.intl.datetimeIntl;
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_user_delete_success" }),
      });
      const response = await fetchUserList(state.router.route?.params);
      const unwrapped = responseUnwrap<PaginatedUserList>(response);
      if (!("error" in unwrapped)) {
        dispatch(userManagementActions.addUserList(unwrapped));
      }
    }
  },
});

// deactivate user
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: userManagementActions.deactivateUser,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const currentRoute = state.router.route;
    await client.apiV1ManagementUserManagementDeactivateUserPartialUpdate(action.payload);
    infoFunc?.({
      title: intl.formatMessage({ id: "notification_user_deactivate_success" }),
    });
    const response = await fetchUserList(currentRoute?.params);
    const unwrapped = responseUnwrap<PaginatedUserList>(response);
    if (!("error" in unwrapped)) {
      dispatch(userManagementActions.addUserList(unwrapped));
    }
  },
});

// reactivate user
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: userManagementActions.reactivateUser,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const currentRoute = state.router.route;
    await client.apiV1ManagementUserManagementReactivateUserPartialUpdate(action.payload);
    infoFunc?.({
      title: intl.formatMessage({ id: "notification_user_reactivate_success" }),
    });
    const response = await fetchUserList(currentRoute?.params);
    const unwrapped = responseUnwrap<PaginatedUserList>(response);
    if (!("error" in unwrapped)) {
      dispatch(userManagementActions.addUserList(unwrapped));
    }
  },
});

// load materials list when navigating to materials list page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.SETTINGS_MATERIAL_LIST),
  effect: async (action, { dispatch, getState }) => {
    const navigateAction = action as NavigateAction;
    const state = getState();

    const response = await client.apiV1ManagementMaterialsList(
      navigateAction.payload.route.params.category,
      navigateAction.payload.route.params.page,
      undefined,
      getPerPage(navigateAction.payload.route.params),
      undefined,
      navigateAction.payload.route.params.search
    );
    const unwrapped = responseUnwrap<PaginatedMaterialsList>(response);
    if (!("error" in unwrapped)) {
      dispatch(materialsActions.addMaterialList(unwrapped));
    }
  },
});

// fetch single material when navigating to edit page
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.SETTINGS_MATERIAL_EDIT),
  effect: async (action, { dispatch }) => {
    dispatch(materialsActions.removeMaterial());
    const navigateAction = action as NavigateAction;
    const response = await client.apiV1ManagementMaterialsRetrieve(
      navigateAction.payload.route.params.id
    );
    const unwrapped = responseUnwrap<Materials>(response);
    if ("error" in unwrapped) {
      dispatch(pageLoadErrorFor(navigateAction, unwrapped));
      return;
    }
    dispatch(materialsActions.storeMaterial(unwrapped));
  },
});

// clear material when navigating to create page
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.SETTINGS_MATERIAL_CREATE),
  effect: async (_action, { dispatch }) => {
    dispatch(materialsActions.removeMaterial());
  },
});

// create or update material
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: materialsActions.addOrUpdateMaterial,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;

    if (!action.payload.id) {
      const response = await client.apiV1ManagementMaterialsCreate(action.payload);
      const unwrapped = responseUnwrap<Materials>(response);
      if (!("error" in unwrapped)) {
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_material_create_success",
          }),
        });
        dispatch(routerActions.navigateTo(routeNames.SETTINGS_MATERIAL_LIST));
      }
    } else {
      const response = await client.apiV1ManagementMaterialsPartialUpdate(
        action.payload.id,
        action.payload
      );
      const unwrapped = responseUnwrap<Materials>(response);
      if (!("error" in unwrapped)) {
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_material_update_success",
          }),
        });
        dispatch(routerActions.navigateTo(routeNames.SETTINGS_MATERIAL_LIST));
      }
    }
  },
});

// delete material
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: materialsActions.deleteMaterial,
  effect: async (action, { dispatch, getState }) => {
    const deleteResponse = await client.apiV1ManagementMaterialsDestroy(action.payload);
    if (deleteResponse.status === 204) {
      const state = getState();
      const infoFunc = state.appFeedback.notification?.info;
      const intl = state.intl.datetimeIntl;
      infoFunc?.({
        title: intl.formatMessage({
          id: "notification_material_delete_success",
        }),
      });
      const response = await client.apiV1ManagementMaterialsList();
      const unwrapped = responseUnwrap<PaginatedMaterialsList>(response);
      if (!("error" in unwrapped)) {
        dispatch(materialsActions.addMaterialList(unwrapped));
      }
    }
  },
});

// load services list when navigating to services list page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.SETTINGS_SERVICE_LIST),
  effect: async (action, { dispatch, getState }) => {
    const navigateAction = action as NavigateAction;
    const state = getState();

    const response = await client.apiV1ManagementServicesList(
      navigateAction.payload.route.params.page,
      undefined,
      getPerPage(navigateAction.payload.route.params),
      navigateAction.payload.route.params.search
    );
    const unwrapped = responseUnwrap<PaginatedServicesList>(response);
    if (!("error" in unwrapped)) {
      dispatch(servicesActions.addServiceList(unwrapped));
    }
  },
});

// fetch single service when navigating to edit or view page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.SETTINGS_SERVICE_EDIT) ||
    isNavigateAction(action as NavigateAction, routeNames.SETTINGS_SERVICE_VIEW),
  effect: async (action, { dispatch, getState }) => {
    dispatch(servicesActions.removeService());
    const navigateAction = action as NavigateAction;
    const response = await client.apiV1ManagementServicesRetrieve(
      navigateAction.payload.route.params.id
    );
    const unwrapped = responseUnwrap<Services>(response);
    if ("error" in unwrapped) {
      dispatch(pageLoadErrorFor(navigateAction, unwrapped));
      return;
    }
    dispatch(servicesActions.storeService(unwrapped));

    const state = getState();
    if (
      state.materials.list.results === undefined ||
      state.materials.list.results.length === 0
    ) {
      const materialsResponse = await client.apiV1ManagementMaterialsList(
        undefined,
        undefined,
        undefined,
        UNPAGED_LIMIT
      );
      const unwrappedMaterials =
        responseUnwrap<PaginatedMaterialsList>(materialsResponse);
      if (!("error" in unwrappedMaterials)) {
        dispatch(materialsActions.addMaterialList(unwrappedMaterials));
      }
    }
  },
});

// clear service when navigating to create page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.SETTINGS_SERVICE_CREATE),
  effect: async (_action, { dispatch, getState }) => {
    dispatch(servicesActions.removeService());

    const state = getState();
    if (
      state.materials.list.results === undefined ||
      state.materials.list.results.length === 0
    ) {
      const materialsResponse = await client.apiV1ManagementMaterialsList(
        undefined,
        undefined,
        undefined,
        UNPAGED_LIMIT
      );
      const unwrappedMaterials =
        responseUnwrap<PaginatedMaterialsList>(materialsResponse);
      if (!("error" in unwrappedMaterials)) {
        dispatch(materialsActions.addMaterialList(unwrappedMaterials));
      }
    }
  },
});

// create or update service
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: servicesActions.addOrUpdateService,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;

    if (!action.payload.id) {
      const response = await client.apiV1ManagementServicesCreate(action.payload);
      const unwrapped = responseUnwrap<Services>(response);
      if (!("error" in unwrapped)) {
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_service_create_success",
          }),
        });
        dispatch(routerActions.navigateTo(routeNames.SETTINGS_SERVICE_LIST));
      }
    } else {
      const response = await client.apiV1ManagementServicesPartialUpdate(
        action.payload.id,
        action.payload
      );
      const unwrapped = responseUnwrap<Services>(response);
      if (!("error" in unwrapped)) {
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_service_update_success",
          }),
        });
        dispatch(
          routerActions.navigateTo(routeNames.SETTINGS_SERVICE_VIEW, {
            id: action.payload.id,
          })
        );
      }
    }
  },
});

// delete service
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: servicesActions.deleteService,
  effect: async (action, { dispatch, getState }) => {
    const deleteResponse = await client.apiV1ManagementServicesDestroy(action.payload);
    if (deleteResponse.status === 204) {
      const state = getState();
      const infoFunc = state.appFeedback.notification?.info;
      const intl = state.intl.datetimeIntl;
      infoFunc?.({
        title: intl.formatMessage({
          id: "notification_service_delete_success",
        }),
      });
      const response = await client.apiV1ManagementServicesList();
      const unwrapped = responseUnwrap<PaginatedServicesList>(response);
      if (!("error" in unwrapped)) {
        dispatch(servicesActions.addServiceList(unwrapped));
      }
    }
  },
});

// creates tokens on login
// listener.startListening.withTypes<AppDispatch>()({
// 	actionCreator: tokenActions.doLogin,
// 	effect: async (action, { dispatch }) => {
// 		const loginResponse = await client.apiV1AuthLoginCreate({
// 			username: action.payload.username,
// 			password: action.payload.password,
// 			totp: action.payload.totp,
// 		});
// 		localStorage.setItem("access", loginResponse.data.access);
// 		localStorage.setItem("refresh", loginResponse.data.refresh);
// 		dispatch(tokenActions.addToken(loginResponse.data));

// 		dispatch(routerActions.navigateTo(routeNames.TICKET_LIST));
// 	},
// });

// toggle report_done on ticket view
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.setReportDone,
  effect: async (action, { dispatch }) => {
    const { id, value } = action.payload;
    const res = await client.apiV1ManagementTicketPartialUpdate(id, {
      report_done: value,
    });
    if (res.status === 200) {
      const ticketRes = await client.apiV1ManagementTicketRetrieve(id);
      const unwrapped = responseUnwrap<TicketRetrieve>(ticketRes);
      if (!("error" in unwrapped)) {
        dispatch(ticketActions.storeTicket(unwrapped));
      }
    }
  },
});

// set rating on ticket view
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: ticketActions.setRating,
  effect: async (action, { dispatch }) => {
    const { id, value, message } = action.payload;
    const res = await client.apiV1ManagementTicketPartialUpdate(
      id,
      message === undefined ? { rating: value } : { rating: value, rating_message: message }
    );
    if (res.status === 200) {
      const ticketRes = await client.apiV1ManagementTicketRetrieve(id);
      const unwrapped = responseUnwrap<TicketRetrieve>(ticketRes);
      if (!("error" in unwrapped)) {
        dispatch(ticketActions.storeTicket(unwrapped));
      }
    }
  },
});

// fetch user profile and groups when navigating to profile view or edit
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.USER_PROFILE_VIEW) ||
    isNavigateAction(action as NavigateAction, routeNames.USER_PROFILE_EDIT),
  effect: async (action, { dispatch }) => {
    dispatch(userActions.clearViewedProfile());
    const navigateAction = action as NavigateAction;
    const profileId = navigateAction.payload.route.params.id;
    if (!profileId) return;
    const [profileRes, groupsRes] = await Promise.all([
      client.apiV1ManagementUsersRetrieve(profileId),
      client.apiV1ManagementMetaGroupsList(),
    ]);
    const unwrappedGroups = responseUnwrap<UserGroup[]>(groupsRes);
    if (!("error" in unwrappedGroups)) {
      dispatch(userActions.storeGroups(unwrappedGroups));
    }
    const unwrappedProfile = responseUnwrap<UserProfile>(profileRes);
    if ("error" in unwrappedProfile) {
      dispatch(pageLoadErrorFor(navigateAction, unwrappedProfile));
      return;
    }
    dispatch(userActions.storeViewedProfile(unwrappedProfile));
  },
});

// clears state on logout
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.USER_LOGOUT),
  effect: async (_action, { dispatch }) => {
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");

    // should be implemented using router
    window.location.replace("/login");
    dispatch(
      routerActions.navigateTo(routeNames.USER_LOGIN, undefined, {
        reload: true,
      })
    );
  },
});

// redirects to login when no tokens
// listener.startListening.withTypes<RootState, AppDispatch>()({
// 	predicate: (action) =>
// 		isNavigateAction(action as NavigateAction, routeNames.USER_CURRENT),
// 	effect: async (_actions, { dispatch, getState }) => {
// 		const state = getState();

// 	},
// });

// fetch project list when navigating to project list page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.PROJECT_LIST),
  effect: async (action, { dispatch, getState }) => {
    const navigateAction = action as NavigateAction;
    const state = getState();

    const [projectResponse, clientResponse] = await Promise.all([
      client.apiV1ManagementProjectsList(
        navigateAction.payload.route.params.client,
        navigateAction.payload.route.params.page,
        navigateAction.payload.route.params.name,
        navigateAction.payload.route.params.sort,
        getPerPage(navigateAction.payload.route.params),
        navigateAction.payload.route.params.project_status,
        navigateAction.payload.route.params.search
      ),
      client.apiV1ManagementClientsList(
        undefined,
        undefined,
        undefined,
        UNPAGED_LIMIT
      ),
    ]);

    const unwrappedProjects = responseUnwrap<PaginatedProjectWithWorkOrderCountsList>(projectResponse);
    if (!("error" in unwrappedProjects)) {
      dispatch(projectActions.addProjectList(unwrappedProjects));
    }

    const unwrappedClients = responseUnwrap<PaginatedClientList>(clientResponse);
    if (!("error" in unwrappedClients)) {
      dispatch(projectActions.addClients(unwrappedClients.results));
    }
  },
});

// fetch project on edit or view action
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.PROJECT_EDIT) ||
    isNavigateAction(action as NavigateAction, routeNames.PROJECT_VIEW),
  effect: async (action, { dispatch }) => {
    dispatch(projectActions.removeProject());
    const navigateAction = action as NavigateAction;
    const projectId = navigateAction.payload.route.params.id;
    if (!projectId) return;

    const projectResponse = await client.apiV1ManagementProjectsRetrieve(projectId);
    const unwrapped = responseUnwrap<ProjectDetailed>(projectResponse);
    if ("error" in unwrapped) {
      dispatch(pageLoadErrorFor(navigateAction, unwrapped));
      return;
    }
    dispatch(projectActions.storeProject(unwrapped));

    const clientResponse = await client.apiV1ManagementClientsList(
      undefined,
      undefined,
      undefined,
      UNPAGED_LIMIT
    );
    if (clientResponse) {
      const unwrappedClients = responseUnwrap<PaginatedClientList>(clientResponse);
      if (!("error" in unwrappedClients)) {
        dispatch(projectActions.addClients(unwrappedClients.results));
      }
    }

    if (isNavigateAction(action as NavigateAction, routeNames.PROJECT_VIEW)) {
      dispatch(ticketDataActions.fetchTicketData({}));
    }
  },
});

// clear project when navigating to create page, and load client list
listener.startListening.withTypes<AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.PROJECT_CREATE),
  effect: async (_action, { dispatch }) => {
    dispatch(projectActions.removeProject());
    const clientResponse = await client.apiV1ManagementClientsList(
      undefined,
      undefined,
      undefined,
      UNPAGED_LIMIT
    );
    const unwrappedClients = responseUnwrap<PaginatedClientList>(clientResponse);
    if (!("error" in unwrappedClients)) {
      dispatch(projectActions.addClients(unwrappedClients.results));
    }
  },
});

// create or update project
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: projectActions.addOrUpdateProject,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;

    if (!action.payload.id) {
      const response = await client.apiV1ManagementProjectsCreate(action.payload);
      const unwrapped = responseUnwrap<Project>(response);
      if (!("error" in unwrapped)) {
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_project_create_success",
          }),
        });
        dispatch(routerActions.navigateTo(routeNames.PROJECT_LIST));
      }
    } else {
      const response = await client.apiV1ManagementProjectsPartialUpdate(
        action.payload.id,
        action.payload
      );
      const unwrapped = responseUnwrap<Project>(response);
      if (!("error" in unwrapped)) {
        infoFunc?.({
          title: intl.formatMessage({
            id: "notification_project_update_success",
          }),
        });
        dispatch(
          routerActions.navigateTo(routeNames.PROJECT_VIEW, {
            id: action.payload.id,
          })
        );
      }
    }
  },
});

// delete project
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: projectActions.deleteProject,
  effect: async (action, { dispatch, getState }) => {
    const deleteResponse = await client.apiV1ManagementProjectsDestroy(action.payload);
    if (deleteResponse.status === 204) {
      const state = getState();
      const infoFunc = state.appFeedback.notification?.info;
      const intl = state.intl.datetimeIntl;
      infoFunc?.({
        title: intl.formatMessage({
          id: "notification_project_delete_success",
        }),
      });

      const currentRoute = state.router.route;
      const isOnProjectList = currentRoute?.name === routeNames.PROJECT_LIST;
      const isOnProjectView =
        currentRoute?.name === routeNames.PROJECT_VIEW ||
        currentRoute?.name === routeNames.PROJECT_EDIT;

      if (isOnProjectView) {
        dispatch(routerActions.navigateTo(routeNames.PROJECT_LIST));
      } else if (isOnProjectList) {
        const response = await client.apiV1ManagementProjectsList(
          currentRoute?.params?.client,
          currentRoute?.params?.page,
          currentRoute?.params?.name,
          currentRoute?.params?.sort,
          getPerPage(currentRoute?.params ?? {}),
          currentRoute?.params?.project_status,
          currentRoute?.params?.search
        );
        const unwrapped = responseUnwrap<PaginatedProjectWithWorkOrderCountsList>(response);
        if (!("error" in unwrapped)) {
          dispatch(projectActions.addProjectList(unwrapped));
        }
      }
    }
  },
});

// inline start/deadline edit from the project preview work order table: PATCH only
// the changed field, then refetch the project because its tickets are sideloaded on
// the project retrieve and are not part of the work order list slice
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: projectActions.projectTicketDateEdit,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { projectId, ticketId, field, value } = action.payload;

    const response = await client.apiV1ManagementTicketPartialUpdate(ticketId, {
      [field]: value,
    });
    const unwrapped = responseUnwrap<Ticket>(response);
    if ("error" in unwrapped) {
      handleErrorNotification(state, unwrapped);
      return;
    }

    const refreshed = await client.apiV1ManagementProjectsRetrieve(projectId);
    const unwrappedRefresh = responseUnwrap<ProjectDetailed>(refreshed);
    if (!("error" in unwrappedRefresh)) {
      dispatch(projectActions.storeProject(unwrappedRefresh));
    }

    infoFunc?.({
      title: intl.formatMessage({ id: "notification_ticket_update_success" }),
    });
  },
});

// inline assignee edit from the project preview work order table, refetching the
// project for the same reason as the inline date edit above
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: projectActions.projectTicketAssigneesEdit,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { projectId, ticketId, assigneeIds } = action.payload;

    const response = await client.apiV1ManagementTicketPartialUpdate(ticketId, {
      assignees: assigneeIds.map((assignee) => ({ assignee })),
    });
    const unwrapped = responseUnwrap<Ticket>(response);
    if ("error" in unwrapped) {
      handleErrorNotification(state, unwrapped);
      return;
    }

    const refreshed = await client.apiV1ManagementProjectsRetrieve(projectId);
    const unwrappedRefresh = responseUnwrap<ProjectDetailed>(refreshed);
    if (!("error" in unwrappedRefresh)) {
      dispatch(projectActions.storeProject(unwrappedRefresh));
    }

    infoFunc?.({
      title: intl.formatMessage({ id: "notification_ticket_update_success" }),
    });
  },
});

// deleting a work order from the project page refreshes the project, whose table
// is served by the project retrieve rather than the work order list slice
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: projectActions.projectTicketDelete,
  effect: async (action, { dispatch, getState }) => {
    const { projectId, ticketId } = action.payload;
    const deleteResponse = await client.apiV1ManagementTicketDestroy(ticketId);
    if (deleteResponse.status !== 204) return;

    const state = getState();
    state.appFeedback.notification?.info({
      title: state.intl.datetimeIntl.formatMessage({
        id: "notification_ticket_delete_success",
      }),
    });

    const refreshed = await client.apiV1ManagementProjectsRetrieve(projectId);
    const unwrappedRefresh = responseUnwrap<ProjectDetailed>(refreshed);
    if (!("error" in unwrappedRefresh)) {
      dispatch(projectActions.storeProject(unwrappedRefresh));
    }
  },
});

// mark project as delayed: the project status PATCH is the whole operation — the backend
// cascades the project's NEW/IN_PROGRESS/STANDBY work orders to DELAYED, recording each
// one's previous status server-side, so nothing is tracked or patched here
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: projectActions.markProjectDelayed,
  effect: async (action, { dispatch, getState }) => {
    const projectId = action.payload;
    const state = getState();
    const intl = state.intl.datetimeIntl;
    const infoFunc = state.appFeedback.notification?.info;

    const projectResponse = await client.apiV1ManagementProjectsPartialUpdate(projectId, {
      project_status: "DELAYED",
    });
    const unwrappedProject = responseUnwrap<Project>(projectResponse);
    if ("error" in unwrappedProject) return;

    const refreshed = await client.apiV1ManagementProjectsRetrieve(projectId);
    const unwrappedRefresh = responseUnwrap<ProjectDetailed>(refreshed);
    if (!("error" in unwrappedRefresh)) {
      dispatch(projectActions.storeProject(unwrappedRefresh));
    }

    infoFunc?.({
      title: intl.formatMessage({ id: "notification_project_marked_delayed" }),
      showProgress: true,
      pauseOnHover: true,
    });
  },
});

// resume project: setting it back to ACTIVE continues the work orders the cascade
// delayed, each to the status it held before — again entirely server-side
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: projectActions.resumeProject,
  effect: async (action, { dispatch, getState }) => {
    const projectId = action.payload;
    const state = getState();
    const intl = state.intl.datetimeIntl;
    const infoFunc = state.appFeedback.notification?.info;

    const projectResponse = await client.apiV1ManagementProjectsPartialUpdate(projectId, {
      project_status: "ACTIVE",
    });
    const unwrappedProject = responseUnwrap<Project>(projectResponse);
    if ("error" in unwrappedProject) return;

    const refreshed = await client.apiV1ManagementProjectsRetrieve(projectId);
    const unwrappedRefresh = responseUnwrap<ProjectDetailed>(refreshed);
    if (!("error" in unwrappedRefresh)) {
      dispatch(projectActions.storeProject(unwrappedRefresh));
    }

    infoFunc?.({
      title: intl.formatMessage({ id: "notification_project_resumed" }),
      showProgress: true,
      pauseOnHover: true,
    });
  },
});

// update user profile
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: userActions.updateProfile,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { profileId, payload } = action.payload;

    const res = await client.apiV1ManagementUsersUpdatePartialUpdate(profileId, payload);
    dispatch(userActions.setUpdateProfileLoading(false));

    const unwrapped = responseUnwrap<UserProfile>(res);
    if (!("error" in unwrapped)) {
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_profile_update_success" }),
      });
      dispatch(routerActions.navigateTo(routeNames.USER_PROFILE_VIEW, { id: profileId }));
    }
  },
});

// change password
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: userActions.changePassword,
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const infoFunc = state.appFeedback.notification?.info;
    const intl = state.intl.datetimeIntl;
    const { profileId, oldPassword, newPassword } = action.payload;

    dispatch(userActions.setChangePasswordStatus("loading"));
    const res = await client.apiV1ManagementUsersChangePasswordCreate(profileId, {
      old: oldPassword,
      new: newPassword,
    });
    if (res.status === 200) {
      infoFunc?.({
        title: intl.formatMessage({ id: "notification_password_change_success" }),
      });
      dispatch(userActions.setChangePasswordStatus("succeeded"));
    } else {
      dispatch(userActions.setChangePasswordStatus("failed"));
    }
  },
});

// load the dashboard and its project filter options when opening the dashboard
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) => isNavigateAction(action as NavigateAction, routeNames.DASHBOARD),
  effect: async (action, { dispatch }) => {
    const navigateAction = action as NavigateAction;
    dispatch(ticketDataActions.fetchTicketData({}));
    dispatch(
      dashboardActions.fetchDashboard({
        project: navigateAction.payload.route.params.project || undefined,
      })
    );
  },
});

// Only the latest request may store its result, so a slower response for a
// previously selected project cannot overwrite the current one.
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: dashboardActions.fetchDashboard,
  effect: async (action, { dispatch, cancelActiveListeners, signal }) => {
    cancelActiveListeners();
    const response = await client.apiV1ManagementDashboardSummaryRetrieve(
      action.payload.project,
      { signal }
    );
    if (signal.aborted) return;
    const unwrappedSummary = responseUnwrap<DashboardSummary>(response);
    if ("error" in unwrappedSummary) {
      dispatch(dashboardActions.setDashboardFailed());
      return;
    }
    dispatch(dashboardActions.storeDashboard(unwrappedSummary));
  },
});

// technician bookings shown on the work order's Schedule tab
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.TICKET_VIEW),
  effect: async (action, { dispatch }) => {
    const ticketId = (action as NavigateAction).payload.route.params.id;
    dispatch(assignmentScheduleActions.fetchTechnicians());
    dispatch(assignmentScheduleActions.fetchTicketSchedules(ticketId));
  },
});

listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.PROJECT_VIEW),
  effect: async (_action, { dispatch }) => {
    dispatch(assignmentScheduleActions.fetchTechnicians());
  },
});

listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: assignmentScheduleActions.fetchTechnicians,
  effect: async (_action, { dispatch, getState }) => {
    if (getState().assignmentSchedule.technicians !== null) return;
    const response = await client.apiV1ManagementUsersList(
      undefined,
      undefined,
      "technician",
      true,
      UNPAGED_LIMIT
    );
    const techniciansData = response.data as unknown as { results?: UserProfile[] };
    dispatch(assignmentScheduleActions.storeTechnicians(techniciansData.results ?? []));
  },
});

listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: assignmentScheduleActions.fetchTicketSchedules,
  effect: async (action, { dispatch }) => {
    const ticketId = action.payload;
    const response = await client.apiV1ManagementAssignmentSchedulesList(
      undefined,
      undefined,
      undefined,
      "start_date",
      UNPAGED_LIMIT,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      ticketId
    );
    const unwrapped = responseUnwrap<PaginatedTechnicianAssignmentScheduleList>(response);
    if (!("error" in unwrapped)) {
      dispatch(
        assignmentScheduleActions.storeTicketSchedules({
          ticketId,
          schedules: unwrapped.results,
        })
      );
    }
  },
});

// The booking form asks for the chosen technicians' bookings in the chosen range on
// every change; only the latest answer may land.
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: assignmentScheduleActions.fetchOtherBookings,
  effect: async (action, { dispatch, cancelActiveListeners, signal }) => {
    cancelActiveListeners();
    const { technicianIds, startDate, endDate } = action.payload;
    if (technicianIds.length === 0) {
      dispatch(assignmentScheduleActions.clearOtherBookings());
      return;
    }
    const response = await client.apiV1ManagementAssignmentSchedulesList(
      undefined,
      startDate,
      undefined,
      "start_date",
      UNPAGED_LIMIT,
      undefined,
      endDate,
      ["SCHEDULED", "ACTIVE", "ENDING"],
      undefined,
      technicianIds,
      undefined,
      { signal }
    );
    if (signal.aborted) return;
    const unwrapped = responseUnwrap<PaginatedTechnicianAssignmentScheduleList>(response);
    dispatch(
      assignmentScheduleActions.storeOtherBookings(
        "error" in unwrapped ? [] : unwrapped.results
      )
    );
  },
});

// A booking that starts today assigns the technician straight away, so the page the
// change was made from reloads its assignees along with the bookings.
async function refreshAfterScheduleChange(
  dispatch: AppDispatch,
  getState: () => RootState,
  source: ScheduleChangeSource
) {
  const boardQuery = getState().assignmentSchedule.boardQuery;
  if (boardQuery) {
    dispatch(assignmentScheduleActions.fetchBoard(boardQuery));
  }
  if ("board" in source) return;
  if ("ticketId" in source) {
    dispatch(assignmentScheduleActions.fetchTicketSchedules(source.ticketId));
    dispatch(ticketActions.refreshTicketHistory(source.ticketId));
    const ticketResponse = await client.apiV1ManagementTicketRetrieve(source.ticketId);
    const unwrappedTicket = responseUnwrap<TicketRetrieve>(ticketResponse);
    if (!("error" in unwrappedTicket)) {
      dispatch(ticketActions.storeTicket(unwrappedTicket));
    }
    return;
  }
  const projectResponse = await client.apiV1ManagementProjectsRetrieve(source.projectId);
  const unwrappedProject = responseUnwrap<ProjectDetailed>(projectResponse);
  if (!("error" in unwrappedProject)) {
    dispatch(projectActions.storeProject(unwrappedProject));
  }
}

listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: assignmentScheduleActions.scheduleTechnicians,
  effect: async (action, { dispatch, getState }) => {
    const { technicianIds, ticketIds, startDate, endDate, source } = action.payload;
    const response = await client.apiV1ManagementAssignmentSchedulesBulkCreate({
      technicians: technicianIds,
      tickets: ticketIds,
      start_date: startDate,
      end_date: endDate,
    });
    const unwrapped = responseUnwrap<TechnicianAssignmentSchedule[]>(response);
    if ("error" in unwrapped) {
      dispatch(assignmentScheduleActions.setSaveStatus("failed"));
      return;
    }
    const state = getState();
    state.appFeedback.notification?.info?.({
      title: state.intl.datetimeIntl.formatMessage(
        { id: "schedule_created_success" },
        { count: unwrapped.length }
      ),
    });
    dispatch(assignmentScheduleActions.setSaveStatus("succeeded"));
    await refreshAfterScheduleChange(dispatch, getState, source);
  },
});

listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: assignmentScheduleActions.updateScheduleDates,
  effect: async (action, { dispatch, getState }) => {
    const { scheduleId, startDate, endDate, source } = action.payload;
    const response = await client.apiV1ManagementAssignmentSchedulesPartialUpdate(
      scheduleId,
      { start_date: startDate, end_date: endDate }
    );
    const unwrapped = responseUnwrap<TechnicianAssignmentSchedule>(response);
    if ("error" in unwrapped) {
      dispatch(assignmentScheduleActions.setSaveStatus("failed"));
      return;
    }
    dispatch(assignmentScheduleActions.setSaveStatus("succeeded"));
    await refreshAfterScheduleChange(dispatch, getState, source);
  },
});

listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: assignmentScheduleActions.cancelSchedule,
  effect: async (action, { dispatch, getState }) => {
    const { scheduleId, source } = action.payload;
    const response = await client.apiV1ManagementAssignmentSchedulesCancelCreate(scheduleId);
    if ("isAxiosError" in response) return;
    await refreshAfterScheduleChange(dispatch, getState, source);
  },
});

// technician planning board: what it needs besides the bookings themselves
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.SCHEDULE),
  effect: async (_action, { dispatch }) => {
    dispatch(assignmentScheduleActions.fetchTechnicians());
    dispatch(assignmentScheduleActions.fetchBookableWorkOrders());
    dispatch(ticketDataActions.fetchTicketData({}));
  },
});

// Bookings overlapping the visible dates plus the dateless manual assignments; a
// newer range or filter replaces a request still in flight.
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: assignmentScheduleActions.fetchBoard,
  effect: async (action, { dispatch, cancelActiveListeners, signal }) => {
    cancelActiveListeners();
    const { startDate, endDate, project, location } = action.payload;
    const [bookingsResponse, manualResponse] = await Promise.all([
      client.apiV1ManagementAssignmentSchedulesList(
        undefined,
        startDate,
        location,
        "start_date",
        UNPAGED_LIMIT,
        project,
        endDate,
        ["SCHEDULED", "ACTIVE", "ENDING", "COMPLETED"],
        undefined,
        undefined,
        undefined,
        { signal }
      ),
      client.apiV1ManagementAssignmentSchedulesManualAssignmentsList(location, project, {
        signal,
      }),
    ]);
    if (signal.aborted) return;
    const bookings =
      responseUnwrap<PaginatedTechnicianAssignmentScheduleList>(bookingsResponse);
    const manualAssignments = responseUnwrap<ManualAssignment[]>(manualResponse);
    dispatch(
      assignmentScheduleActions.storeBoard({
        bookings: "error" in bookings ? [] : bookings.results,
        manualAssignments: "error" in manualAssignments ? [] : manualAssignments,
      })
    );
  },
});

listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: assignmentScheduleActions.fetchBookableWorkOrders,
  effect: async (_action, { dispatch }) => {
    const response =
      await client.apiV1ManagementAssignmentSchedulesBookableWorkOrdersList();
    const unwrapped = responseUnwrap<BookableWorkOrder[]>(response);
    if (!("error" in unwrapped)) {
      dispatch(assignmentScheduleActions.storeBookableWorkOrders(unwrapped));
    }
  },
});

// load the project and site filter options when opening a report
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, (name) =>
      [routeNames.REPORT_MATERIAL_USAGE, routeNames.REPORT_PROJECT].includes(name)
    ),
  effect: async (_action, { dispatch }) => {
    dispatch(ticketDataActions.fetchTicketData({}));
  },
});

// Every filter change asks for a new summary; only the latest request may store its
// result, so an earlier, slower response cannot overwrite the current filters' one.
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: reportsActions.fetchMaterialUsageSummary,
  effect: async (action, { dispatch, cancelActiveListeners, signal }) => {
    cancelActiveListeners();
    const filters = action.payload;
    const response = await client.apiV1ManagementReportsMaterialUsageSummaryRetrieve(
      filters.category,
      filters.dateFrom,
      filters.dateTo,
      filters.includeInProgress,
      filters.location,
      filters.project,
      { signal }
    );
    if (signal.aborted) return;
    const unwrappedSummary = responseUnwrap<MaterialUsageSummary>(response);
    if ("error" in unwrappedSummary) {
      dispatch(reportsActions.setMaterialUsageSummaryFailed());
      return;
    }
    dispatch(
      reportsActions.storeMaterialUsageSummary({ filters, summary: unwrappedSummary })
    );
  },
});

// Downloads the Excel report for the filters of the summary on screen. Those filters
// already passed the server's validation, so the file request has no field errors to
// report (they would arrive as a Blob the error toasts cannot read).
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: reportsActions.downloadMaterialUsageReport,
  effect: async (_action, { dispatch, getState }) => {
    const filters = getState().reports.materialUsageSummaryFilters;
    if (!filters) {
      dispatch(reportsActions.setMaterialUsageDownloadStatus("failed"));
      return;
    }
    const response = await client.apiV1ManagementReportsMaterialUsageRetrieve(
      filters.category,
      filters.dateFrom,
      filters.dateTo,
      filters.includeInProgress,
      filters.location,
      filters.project,
      { responseType: "blob" }
    );
    if ("isAxiosError" in response) {
      dispatch(reportsActions.setMaterialUsageDownloadStatus("failed"));
      return;
    }
    downloadBlob(
      response.data as unknown as Blob,
      filenameFromContentDisposition(response.headers["content-disposition"]) ??
        "supplies-usage.xlsx"
    );
    dispatch(reportsActions.setMaterialUsageDownloadStatus("succeeded"));
  },
});

// as for the material usage summary, only the latest request may store its result
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: reportsActions.fetchProjectReportSummary,
  effect: async (action, { dispatch, cancelActiveListeners, signal }) => {
    cancelActiveListeners();
    const filters = action.payload;
    const response = await client.apiV1ManagementReportsProjectSummaryRetrieve(
      filters.project,
      filters.dateFrom,
      filters.dateTo,
      { signal }
    );
    if (signal.aborted) return;
    const unwrappedSummary = responseUnwrap<ProjectReportSummary>(response);
    if ("error" in unwrappedSummary) {
      dispatch(reportsActions.setProjectReportSummaryFailed());
      return;
    }
    dispatch(
      reportsActions.storeProjectReportSummary({ filters, summary: unwrappedSummary })
    );
  },
});

// downloads the Excel report for the filters of the summary on screen
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: reportsActions.downloadProjectReport,
  effect: async (_action, { dispatch, getState }) => {
    const filters = getState().reports.projectReportSummaryFilters;
    if (!filters) {
      dispatch(reportsActions.setProjectReportDownloadStatus("failed"));
      return;
    }
    const response = await client.apiV1ManagementReportsProjectRetrieve(
      filters.project,
      filters.dateFrom,
      filters.dateTo,
      { responseType: "blob" }
    );
    if ("isAxiosError" in response) {
      dispatch(reportsActions.setProjectReportDownloadStatus("failed"));
      return;
    }
    downloadBlob(
      response.data as unknown as Blob,
      filenameFromContentDisposition(response.headers["content-disposition"]) ??
        "project-report.xlsx"
    );
    dispatch(reportsActions.setProjectReportDownloadStatus("succeeded"));
  },
});

// the technician filter options of the technicians report
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.REPORT_TECHNICIANS),
  effect: async (_action, { dispatch }) => {
    dispatch(assignmentScheduleActions.fetchTechnicians());
  },
});

// as for the material usage summary, only the latest request may store its result
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: reportsActions.fetchTechniciansReportSummary,
  effect: async (action, { dispatch, cancelActiveListeners, signal }) => {
    cancelActiveListeners();
    const filters = action.payload;
    const response = await client.apiV1ManagementReportsTechniciansSummaryRetrieve(
      filters.dateFrom,
      filters.dateTo,
      filters.technicians,
      { signal }
    );
    if (signal.aborted) return;
    const unwrappedSummary = responseUnwrap<TechniciansReportSummary>(response);
    if ("error" in unwrappedSummary) {
      dispatch(reportsActions.setTechniciansReportSummaryFailed());
      return;
    }
    dispatch(
      reportsActions.storeTechniciansReportSummary({ filters, summary: unwrappedSummary })
    );
  },
});

// downloads the Excel report for the filters of the summary on screen
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: reportsActions.downloadTechniciansReport,
  effect: async (_action, { dispatch, getState }) => {
    const filters = getState().reports.techniciansReportSummaryFilters;
    if (!filters) {
      dispatch(reportsActions.setTechniciansReportDownloadStatus("failed"));
      return;
    }
    const response = await client.apiV1ManagementReportsTechniciansRetrieve(
      filters.dateFrom,
      filters.dateTo,
      filters.technicians,
      { responseType: "blob" }
    );
    if ("isAxiosError" in response) {
      dispatch(reportsActions.setTechniciansReportDownloadStatus("failed"));
      return;
    }
    downloadBlob(
      response.data as unknown as Blob,
      filenameFromContentDisposition(response.headers["content-disposition"]) ??
        "technicians-report.xlsx"
    );
    dispatch(reportsActions.setTechniciansReportDownloadStatus("succeeded"));
  },
});

// load WO manager notifications when navigating to the notifications page
listener.startListening.withTypes<RootState, AppDispatch>()({
  predicate: (action) =>
    isNavigateAction(action as NavigateAction, routeNames.NOTIFICATIONS),
  effect: async (_action, { dispatch }) => {
    dispatch(notificationActions.fetchNotifications());
    try {
      const [notificationsRes, techniciansRes] = await Promise.all([
        client.apiV1ManagementNotificationsList(
          undefined,
          undefined,
          undefined,
          "-created_at",
          UNPAGED_LIMIT
        ),
        client.apiV1ManagementUsersList(
          undefined,
          undefined,
          "technician",
          true,
          UNPAGED_LIMIT
        ),
      ]);

      const notificationsData = notificationsRes.data as unknown as {
        results?: NotificationList[];
      };
      const techniciansData = techniciansRes.data as unknown as {
        results?: UserProfile[];
      };

      dispatch(
        notificationActions.storeNotifications({
          list: notificationsData.results ?? [],
          technicianIds: (techniciansData.results ?? []).map((u) => u.id),
        })
      );
    } catch (err) {
      dispatch(
        notificationActions.setNotificationsError(
          err instanceof Error ? err.message : "Failed to load notifications"
        )
      );
    }
  },
});

// mark a group of WO manager notifications as read
listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: notificationActions.requestMarkRead,
  effect: async (action, { dispatch }) => {
    dispatch(notificationActions.markNotificationsRead(action.payload));
    await Promise.all(
      action.payload.map((id) =>
        client.apiV1ManagementNotificationsMarkAsReadPartialUpdate(id)
      )
    );
  },
});

// Initialise router
export const routerInstance = createRouter(routes, {
  allowNotFound: false,
});
// biome-ignore lint/correctness/useHookAtTopLevel: This is not a hook
routerInstance.usePlugin(browserPluginFactory());

export function confStore() {
  return configureStore({
    reducer: {
      router: router5Reducer,
      ticket: ticketReducer,
      ticketData: ticketDataReducer,
      token: tokenReducer,
      userstate: userSliceReducer,
      client: clientReducer,
      turbine: turbineReducer,
      component: componentReducer,
      appFeedback: appFeedbackSlice.reducer,
      intl: intlSliceReducer,
      location: locationReducer,
      userManagement: userManagementReducer,
      materials: materialsReducer,
      services: servicesReducer,
      project: projectReducer,
      dashboard: dashboardReducer,
      notification: notificationReducer,
      serviceCounter: serviceCounterReducer,
      pageStatus: pageStatusReducer,
      reports: reportsReducer,
      assignmentSchedule: assignmentScheduleReducer,
    },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: ["appFeedback/setAppFeedbackControls"],
          ignoredPaths: ["appFeedback", "intl"],
        },
      })
        .concat(router5Middleware(routerInstance))
        .prepend(listener.middleware),
  });
}
export const store = confStore();
routerInstance.start();
